import type { IconName } from '@w7/ui'
import type { BackendKind, BookWorkspaceId, WorkspaceMode } from './domain'

export interface ModeDescriptor {
  id: WorkspaceMode
  label: string
  /** Name this page carried in the console being replaced. */
  legacyName: string
  description: string
  backend: BackendKind
  /** Exposure vs ticker chrome. */
  surface: 'exposure' | 'ticker'
  icon: IconName
  keywords: string[]
  /** Keep for URL redirect / grants; omit from rail (e.g. Previous New → Previous). */
  hiddenFromNav?: boolean
}

export interface WorkspaceDescriptor {
  id: BookWorkspaceId
  label: string
  /** Rail label — the legacy menu name operators search for. */
  menuLabel: string
  pathPrefix: string
  defaultMode: WorkspaceMode
  modes: ModeDescriptor[]
}

function bookModes(): ModeDescriptor[] {
  return [
    {
      id: 'net-exposure',
      label: 'Net Exposure',
      legacyName: 'Net Exposure',
      description: 'Open sportsbook books — My PT, sport filter, Detail Book',
      backend: 'live-books',
      surface: 'exposure',
      icon: 'net-exposure',
      keywords: ['net exposure', 'open books', 'my pt'],
    },
    {
      id: 'bet-ticker',
      label: 'Bet Ticker',
      legacyName: 'Bet Ticker',
      description: 'Open sportsbook bets, newest first — filter by member, market, side, status or stake',
      backend: 'live-ticker',
      surface: 'ticker',
      icon: 'activity',
      keywords: ['bet ticker', 'open bets', 'matched bets'],
    },
    {
      id: 'previous',
      label: 'Bet Ticker Previous',
      legacyName: 'Bet Ticker Previous',
      description:
        'Settled / cancelled sportsbook bets — PG ≤7 days ending today, else BigQuery (up to 31 days)',
      backend: 'live-ticker',
      surface: 'ticker',
      icon: 'clock',
      keywords: ['previous bets', 'settled', 'history', 'bet ticker previous', 'bigquery'],
    },
    {
      // ponytail: merged into Previous; URL redirects via WorkspacePage Navigate.
      id: 'previous-new',
      label: 'Bet Ticker Previous New',
      legacyName: 'Bet Ticker Previous New',
      description: 'Redirects to Bet Ticker Previous',
      backend: 'live-ticker',
      surface: 'ticker',
      icon: 'clock',
      keywords: ['previous new', 'bet ticker previous new'],
      hiddenFromNav: true,
    },
  ]
}

function premiumModes(): ModeDescriptor[] {
  return [
    {
      id: 'net-exposure',
      label: 'Net Exposure',
      legacyName: 'Net Exposure',
      description: 'Open Premium BM (artemis) books — My PT, sport filter, Detail Book',
      backend: 'live-books',
      surface: 'exposure',
      icon: 'net-exposure',
      keywords: ['premium bm', 'net exposure', 'artemis'],
    },
    {
      id: 'bet-ticker',
      label: 'Bet Ticker',
      legacyName: 'Bet Ticker',
      description: 'Open Premium BM bets, newest first',
      backend: 'live-ticker',
      surface: 'ticker',
      icon: 'activity',
      keywords: ['premium bm', 'bet ticker', 'artemis'],
    },
    {
      id: 'previous',
      label: 'Bet Ticker Previous',
      legacyName: 'Bet Ticker Previous',
      description: 'Settled Premium BM bets — date range up to 31 days',
      backend: 'live-ticker',
      surface: 'ticker',
      icon: 'clock',
      keywords: ['premium bm', 'previous bets', 'artemis'],
    },
  ]
}

export const BOOK_MANAGEMENT_WORKSPACE: WorkspaceDescriptor = {
  id: 'book-management',
  label: 'Book Management',
  menuLabel: 'Book Management',
  pathPrefix: '/book-management',
  defaultMode: 'net-exposure',
  modes: bookModes(),
}

export const PREMIUM_BM_WORKSPACE: WorkspaceDescriptor = {
  id: 'premium-bm',
  label: 'Premium BM Management',
  menuLabel: 'Premium BM Management',
  pathPrefix: '/premium-bm',
  defaultMode: 'net-exposure',
  modes: premiumModes(),
}

/** Rail workspaces. Premium BM Management is defined but omitted until Artemis is integrated. */
export const WORKSPACES: WorkspaceDescriptor[] = [BOOK_MANAGEMENT_WORKSPACE]

export function workspaceById(id: BookWorkspaceId): WorkspaceDescriptor {
  return WORKSPACES.find((ws) => ws.id === id) ?? BOOK_MANAGEMENT_WORKSPACE
}

export function modePath(workspace: BookWorkspaceId, mode: WorkspaceMode): string {
  return `${workspaceById(workspace).pathPrefix}/${mode}`
}

export function parseWorkspaceMode(
  workspace: BookWorkspaceId,
  raw: string | undefined,
): ModeDescriptor | null {
  if (!raw) return null
  return workspaceById(workspace).modes.find((mode) => mode.id === raw) ?? null
}

export function isWorkspaceMode(value: string): value is WorkspaceMode {
  return BOOK_MANAGEMENT_WORKSPACE.modes.some((mode) => mode.id === value)
}
